import React, { useState } from 'react';
import { useParams } from 'react-router';

import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import Stars from '../../../components/Badge/Stars';
import StatusCell from '../../../components/Badge/StatusCell';
import { Modal } from '../../../components/Modal/Modal';
import ExitDispatcherModal from '../../../components/Modal/ModalActions/ExitDispatcherModal/ExitDispatcherModal';
import Tooltip from '../../../components/Tooltip/Tooltip';
import { DriverDetailsDataType } from '../../../hooks/dashboard/driver-list/useDriverDetails';
import { normalizeImageSrc } from '../../../utils/utils';

interface DriverBioAreaProps {
  data: DriverDetailsDataType;
}

const DriverBioArea: React.FC<DriverBioAreaProps> = ({ data }) => {
  const { driverId, name, profilePicture: profilePictureParam } = useParams();
  const [showModal, setShowModal] = useState(false);

  const averageRating = Number(data?.averageRating);
  const timeAtCompany = data?.timeAtCompany;

  const safeAverageRating = Number.isFinite(averageRating) ? averageRating : 0;
  const averageRatingText = Number.isFinite(averageRating)
    ? `${averageRating}`
    : '--';
  const timeAtCompanyText = timeAtCompany ? `${timeAtCompany}` : '--';
  const addressText = data?.address?.trim() || '--';
  const driverProfilePicture =
    normalizeImageSrc(data?.profilePicture) ||
    normalizeImageSrc(profilePictureParam) ||
    '/static/img/profilePlaceholderLargePlain.png';

  return (
    <Container>
      <Img src={driverProfilePicture} alt={`${name ?? 'Driver'} profile`} />
      <TextArea>
        <FirstTextRow>
          <BoldText>
            {data?.firstName} {data?.lastName}
          </BoldText>
          {data?.employmentType ? (
            <StatusCell
              data={{ status: data?.employmentType }}
              statusKey='status'
              customClasses='small-border-radius'
            />
          ) : (
            <LoadingOutlined style={{ fontSize: 16, color: '#999' }} />
          )}
        </FirstTextRow>

        <SecondTextRow>
          <span>Driver</span>
          <VerticalLine></VerticalLine>
          <AddressText>{addressText}</AddressText>
        </SecondTextRow>

        <ThirdTextRow>
          <ThirdTextRowGroup>
            <StarArea>
              <Stars numStars={safeAverageRating} />
              <Tooltip content='This shows the average star rating of a driver based on trips completed within your company.' />
            </StarArea>
            <BoldNumberText>
              <div>{averageRatingText}</div>{' '}
              <div>({data?.totalNumberOfRatings || 0})</div>
            </BoldNumberText>
          </ThirdTextRowGroup>

          <VerticalLine></VerticalLine>

          <ThirdTextRowGroup>
            <div>Average Timeline:</div>
            <BoldNumberText>{timeAtCompanyText}</BoldNumberText>
          </ThirdTextRowGroup>

          <VerticalLine></VerticalLine>

          <ThirdTextRowGroup>
            <div>Status:</div>
            {data?.employmentStatus ? (
              <StatusCell
                data={{ status: data?.employmentStatus }}
                statusKey='status'
                customClasses='small-border-radius'
              />
            ) : (
              <LoadingOutlined style={{ fontSize: 16, color: '#999' }} />
            )}
          </ThirdTextRowGroup>
        </ThirdTextRow>
      </TextArea>

      <Modal showModal={showModal} setShowModal={setShowModal}>
        <ExitDispatcherModal
          data={{ driverId: driverId ?? '', name: name ?? '' }}
        />
      </Modal>
    </Container>
  );
};

export default DriverBioArea;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  padding: 32px;
  display: flex;
  width: fit-content;
  border-radius: 5px;
  gap: 24px;
`;

const TextArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 884px) {
    gap: 8px;
  }
`;

const Img = styled.img`
  width: 91px;
  height: 91px;
  border-radius: 8px;
  overflow: hidden;

  @media (max-width: 884px) {
    min-width: 91px;
    min-height: 91px;
  }
`;

const BoldText = styled.span`
  font-weight: 600;
  font-size: 18px;
  line-height: 18px;
`;

const FirstTextRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SecondTextRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.palette.grayTextColor};
  font-size: 14px;
  line-height: 14px;
  font-weight: 400;
`;

const AddressText = styled.span`
  line-height: 1.4;
  max-width: 600px;
`;

const ThirdTextRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 27px;
  color: ${({ theme }) => theme.palette.grayTextColor};

  @media (max-width: 884px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;

    & > * {
      display: none;
    }

    & > *:not(:nth-of-type(even)) {
      display: flex;
    }
  }
`;

const ThirdTextRowGroup = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 11px;

  @media (max-width: 884px) {
    justify-content: flex-start;
  }
`;

const VerticalLine = styled.div`
  width: 1px;
  height: 16px;
  background-color: ${({ theme }) => theme.palette.borderColor};
`;

const BoldNumberText = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const StarArea = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;
